import { useId, type CSSProperties, type ReactNode } from "react";
import {
  columnsLayout,
  columnsReverseMobileCss,
  type ColumnWidthWeight,
  type LayoutAlign,
  type LayoutGap,
} from "@/lib/cms-puck/layout";

type ColumnsGridProps = {
  columns: 2 | 3 | 4;
  gap: LayoutGap;
  gapCustomPx?: number | null;
  gapRow?: LayoutGap | null;
  gapCol?: LayoutGap | null;
  gapRowCustomPx?: number | null;
  gapColCustomPx?: number | null;
  stackOnMobile: boolean;
  reverseOnMobile?: boolean;
  verticalAlign?: LayoutAlign;
  col1Width?: ColumnWidthWeight;
  col2Width?: ColumnWidthWeight;
  col3Width?: ColumnWidthWeight;
  col4Width?: ColumnWidthWeight;
  children: ReactNode;
};

/**
 * Grille du bloc Columns — `grid-template-columns` toujours en inline style
 * (fiable dans l'iframe Puck). Empilement mobile via `<style>` local + !important.
 * PARITÉ front : inventozen-frontpublic/src/cms-puck/ColumnsGrid.tsx
 */
export function ColumnsGrid({
  children,
  columns,
  gap,
  gapCustomPx,
  gapRow,
  gapCol,
  gapRowCustomPx,
  gapColCustomPx,
  stackOnMobile,
  reverseOnMobile,
  verticalAlign,
  col1Width,
  col2Width,
  col3Width,
  col4Width,
}: ColumnsGridProps) {
  const layout = columnsLayout({
    columns,
    gap,
    gapCustomPx,
    gapRow,
    gapCol,
    gapRowCustomPx,
    gapColCustomPx,
    stackOnMobile,
    verticalAlign,
    col1Width,
    col2Width,
    col3Width,
    col4Width,
  });
  const uid = useId();
  const scopeClass = `cms-cols-${uid.replace(/[^a-zA-Z0-9_-]/g, "") || "x"}`;
  const gridStyle: CSSProperties = {
    gridTemplateColumns: layout.gridTemplateColumns,
    ...layout.gapStyle,
  };
  const mobileCss = [
    stackOnMobile
      ? `@media (max-width:767px){.${scopeClass}{grid-template-columns:minmax(0,1fr)!important}}`
      : "",
    stackOnMobile && reverseOnMobile
      ? columnsReverseMobileCss(scopeClass, columns)
      : "",
  ]
    .filter(Boolean)
    .join("");

  return (
    <>
      {mobileCss ? <style>{mobileCss}</style> : null}
      <div className={`${layout.className} ${scopeClass}`} style={gridStyle}>
        {children}
      </div>
    </>
  );
}
